import { getPlacementRotationDegrees } from "../components/tools/PathPopulateContext";
import { extractSvgInnerContent } from "./channelSplitVector";
import { escapeSvgAttr } from "./affineImageTriangle";
import {
    unionPlacementPayloads,
    type UnionPlacementPayload,
} from "./pathPopulateUnionExport";
import { getSvgDimensions } from "./svgUtils";
import { effectivePressure } from "./artbrushStroke";
import { paletteColorForPathIndex } from "./svgUtils";
import {
    paletteColorIndexForPlacement,
    randomHexColorForPlacement,
} from "./pathPopulatePlacement";

export type StampRotationMode =
    | "align"
    | "random"
    | "random-90"
    | "none"
    | "polar"
    | "polar-inverted";

/** Rotation settings shared by canvas render, export, and union. */
export interface StampRenderOptions {
    objectSize: number;
    anchor: string;
    rotationMode: StampRotationMode;
    baseRotationDeg: number;
    polarCenter: { x: number; y: number } | null;
    pressureSizeEnabled: boolean;
    pressureMinFactor: number;
    objectPlacementMode: "alternating" | "random";
    colorEnabled: boolean;
    colorMode:
        | "single"
        | "random-per-stroke"
        | "random-per-instance"
        | "random-palette-per-instance";
    colorPalette: string[];
    colorSingle: string;
    perpendicularScatterPx: number;
}

export function getStampRotationDegrees(
    stamp: ArtbrushStamp,
    stampIndex: number,
    pathIndex: number,
    object: ParsedStampObject,
    options: StampRenderOptions,
): number {
    const scale = stampObjectScaleFactor(
        object.dimensions,
        options.objectSize,
    ) * Math.max(0.05, stamp.sizeFactor || 1);
    return getPlacementRotationDegrees({
        rotationMode: options.rotationMode,
        baseRotationDeg: options.baseRotationDeg,
        placement: {
            x: stamp.x,
            y: stamp.y,
            angle: (stamp.angleDeg * Math.PI) / 180,
            pathIndex,
        },
        placementIndex: stampIndex,
        polarCenter: options.polarCenter,
        objectDimensions: object.dimensions,
        placementScale: scale,
        anchor: options.anchor,
        scatter: { dx: 0, dy: 0 },
    });
}

export interface StampTrailPoint {
    x: number;
    y: number;
    pressure: number;
    pointerType: string;
}

export interface ArtbrushStamp {
    id: string;
    x: number;
    y: number;
    angleDeg: number;
    objectIndex: number;
    sizeFactor: number;
}

export interface StampStroke {
    id: string;
    stamps: ArtbrushStamp[];
    /** Set when merge-on-stroke-end is enabled and union succeeds. */
    unionPathD?: string;
    unionFill?: string;
}

function applyFillToSvgContent(svgContent: string, fillColor: string): string {
    if (!svgContent) return "";
    const elementRegex =
        /<(path|circle|rect|ellipse|polygon|polyline|line)([^>]*?)(\s*\/)?>/gi;
    return svgContent.replace(
        elementRegex,
        (match, tagName, attributes, selfClose) => {
            const cleanedAttributes = attributes.replace(
                /\s*fill\s*=\s*["'][^"']*["']/gi,
                "",
            );
            const closing = selfClose ? " />" : ">";
            return `<${tagName}${cleanedAttributes} fill="${fillColor}"${closing}`;
        },
    );
}

function stampFillColor(
    options: StampRenderOptions,
    pathIndex: number,
    stampIndex: number,
): string | null {
    if (!options.colorEnabled) return null;
    if (options.colorMode === "single") return options.colorSingle;
    if (options.colorMode === "random-per-instance") {
        return randomHexColorForPlacement(pathIndex, stampIndex);
    }
    if (
        options.colorMode === "random-palette-per-instance" &&
        options.colorPalette.length > 0
    ) {
        const pi = paletteColorIndexForPlacement(
            pathIndex,
            stampIndex,
            options.colorPalette.length,
        );
        return options.colorPalette[pi] ?? options.colorPalette[0] ?? null;
    }
    if (
        options.colorMode === "random-per-stroke" &&
        options.colorPalette.length > 0
    ) {
        return paletteColorForPathIndex(options.colorPalette, pathIndex);
    }
    return null;
}

/** In-progress stroke; `nextStampAt` is arc length of the next placement. */
export interface ActiveStampStroke extends StampStroke {
    trail: StampTrailPoint[];
    arcLength: number;
    nextStampAt: number;
}

function anchorCoords(anchor: string): { x: number; y: number } {
    const map: Record<string, { x: number; y: number }> = {
        "top-left": { x: 0, y: 0 },
        top: { x: 0.5, y: 0 },
        "top-right": { x: 1, y: 0 },
        "center-left": { x: 0, y: 0.5 },
        center: { x: 0.5, y: 0.5 },
        "center-right": { x: 1, y: 0.5 },
        "bottom-left": { x: 0, y: 1 },
        bottom: { x: 0.5, y: 1 },
        "bottom-right": { x: 1, y: 1 },
    };
    return map[anchor] ?? map.center;
}

export interface ParsedStampObject {
    name: string;
    innerContent: string;
    viewBox: string;
    dimensions: { width: number; height: number } | null;
}

export function parseStampObjectSvg(
    name: string,
    content: string,
): ParsedStampObject | null {
    const innerContent = extractSvgInnerContent(content);
    if (!innerContent.trim()) return null;
    const viewBox =
        content.match(/viewBox=["']([^"']+)["']/i)?.[1] ?? "0 0 100 100";
    return {
        name,
        innerContent,
        viewBox,
        dimensions: getSvgDimensions(content),
    };
}

/** Scale so the object's larger viewBox side maps to `objectSize` (px). */
export function stampObjectScaleFactor(
    dimensions: { width: number; height: number } | null,
    objectSize: number,
): number {
    if (!dimensions || objectSize <= 0) return objectSize / 100;
    const maxDim = Math.max(dimensions.width, dimensions.height, 1e-6);
    return objectSize / maxDim;
}

export function stampPlacementTransform(
    stamp: ArtbrushStamp,
    scale: number,
    dimensions: { width: number; height: number } | null,
    anchor: string,
    rotationDeg: number,
    scatter: { dx: number; dy: number } = { dx: 0, dy: 0 },
): string {
    const ac = anchorCoords(anchor);
    let offsetX = 0;
    let offsetY = 0;
    if (dimensions) {
        offsetX = -ac.x * dimensions.width * scale;
        offsetY = -ac.y * dimensions.height * scale;
    }
    return `translate(${stamp.x + scatter.dx}, ${stamp.y + scatter.dy}) rotate(${rotationDeg}) translate(${offsetX}, ${offsetY}) scale(${scale})`;
}

function hashToUnit(pathIndex: number, stampIndex: number): number {
    let h =
        Math.imul(pathIndex | 0, 0x9e3779b1) ^
        Math.imul(stampIndex | 0, 0x85ebca6b) ^
        0x4f2d72a1;
    h >>>= 0;
    h ^= h >>> 16;
    h = Math.imul(h, 0x7feb352d);
    h ^= h >>> 15;
    h = Math.imul(h, 0x846ca68b);
    h ^= h >>> 16;
    return (h >>> 0) / 4294967295;
}

export function getStampPerpendicularScatter(
    stamp: ArtbrushStamp,
    pathIndex: number,
    stampIndex: number,
    maxScatterPx: number,
): { dx: number; dy: number } {
    const max = Math.max(0, maxScatterPx);
    if (max <= 1e-6) return { dx: 0, dy: 0 };
    const unit = hashToUnit(pathIndex, stampIndex);
    const amount = (unit * 2 - 1) * max;
    const normalAngleRad = ((stamp.angleDeg + 90) * Math.PI) / 180;
    return {
        dx: Math.cos(normalAngleRad) * amount,
        dy: Math.sin(normalAngleRad) * amount,
    };
}

function segmentAngleDeg(ax: number, ay: number, bx: number, by: number): number {
    return (Math.atan2(by - ay, bx - ax) * 180) / Math.PI;
}

/**
 * Walk new polyline vertices and place stamps every `spacing` units along arc length.
 */
export function extendStampStrokeWithPoints(
    stroke: ActiveStampStroke,
    newPoints: ReadonlyArray<StampTrailPoint>,
    spacing: number,
    nextStampId: () => string,
    objectCount: number,
    options: Pick<
        StampRenderOptions,
        "pressureSizeEnabled" | "pressureMinFactor" | "objectPlacementMode"
    >,
): ActiveStampStroke {
    if (newPoints.length === 0 || spacing <= 0) return stroke;

    const trail = [...stroke.trail];
    const stamps = [...stroke.stamps];
    let arcLength = stroke.arcLength;
    let nextStampAt = stroke.nextStampAt;

    const chooseObjectIndex = (): number => {
        if (objectCount <= 1) return 0;
        if (options.objectPlacementMode === "random") {
            return Math.floor(Math.random() * objectCount);
        }
        return stamps.length % objectCount;
    };
    const pressureToFactor = (pressure: number, pointerType: string): number => {
        if (!options.pressureSizeEnabled) return 1;
        const t = effectivePressure(pressure, pointerType);
        return options.pressureMinFactor + (1 - options.pressureMinFactor) * t;
    };
    const addStamp = (
        x: number,
        y: number,
        angleDeg: number,
        pressure: number,
        pointerType: string,
    ) => {
        stamps.push({
            id: nextStampId(),
            x,
            y,
            angleDeg,
            objectIndex: chooseObjectIndex(),
            sizeFactor: pressureToFactor(pressure, pointerType),
        });
        nextStampAt += spacing;
    };

    for (const p of newPoints) {
        const last = trail[trail.length - 1];
        if (!last) {
            trail.push(p);
            addStamp(p.x, p.y, 0, p.pressure, p.pointerType);
            continue;
        }

        if (last.x === p.x && last.y === p.y) continue;

        const ax = last.x;
        const ay = last.y;
        const bx = p.x;
        const by = p.y;
        const segLen = Math.hypot(bx - ax, by - ay);
        const angleDeg = segmentAngleDeg(ax, ay, bx, by);

        if (segLen < 1e-9) {
            trail.push(p);
            continue;
        }

        let segStart = 0;
        while (nextStampAt <= arcLength + segLen) {
            const need = nextStampAt - arcLength;
            const t = need / segLen;
            const x = ax + (bx - ax) * t;
            const y = ay + (by - ay) * t;
            const pressure = last.pressure + (p.pressure - last.pressure) * t;
            const pointerType = p.pointerType;
            addStamp(x, y, angleDeg, pressure, pointerType);
            segStart = need;
        }

        arcLength += segLen;
        trail.push(p);
    }

    return { ...stroke, trail, stamps, arcLength, nextStampAt };
}

export function createActiveStampStroke(
    id: string,
    first: StampTrailPoint,
    spacing: number,
    nextStampId: () => string,
    objectCount: number,
    options: Pick<
        StampRenderOptions,
        "pressureSizeEnabled" | "pressureMinFactor" | "objectPlacementMode"
    >,
): ActiveStampStroke {
    const stroke: ActiveStampStroke = {
        id,
        trail: [],
        stamps: [],
        arcLength: 0,
        nextStampAt: 0,
    };
    return extendStampStrokeWithPoints(
        stroke,
        [first],
        spacing,
        nextStampId,
        objectCount,
        options,
    );
}

export function finalizeStampStroke(stroke: ActiveStampStroke): StampStroke {
    return { id: stroke.id, stamps: stroke.stamps };
}

export function stampsToUnionPlacementPayloads(
    stamps: ReadonlyArray<ArtbrushStamp>,
    object: ParsedStampObject,
    options: StampRenderOptions,
    pathIndex: number,
): UnionPlacementPayload[] {
    return stamps.map((stamp, stampIndex) => {
        const scale =
            stampObjectScaleFactor(object.dimensions, options.objectSize) *
            Math.max(0.05, stamp.sizeFactor || 1);
        const rotationDeg = getStampRotationDegrees(
            stamp,
            stampIndex,
            pathIndex,
            object,
            options,
        );
        const scatter = getStampPerpendicularScatter(
            stamp,
            pathIndex,
            stampIndex,
            options.perpendicularScatterPx,
        );
        return {
            transform: stampPlacementTransform(
                stamp,
                scale,
                object.dimensions,
                options.anchor,
                rotationDeg,
                scatter,
            ),
            objectContent: (() => {
                const fillColor = stampFillColor(options, pathIndex, stampIndex);
                return fillColor
                    ? applyFillToSvgContent(object.innerContent, fillColor)
                    : object.innerContent;
            })(),
            viewBox: object.viewBox,
        };
    });
}

export function mergeStampStrokeUnion(
    stamps: ReadonlyArray<ArtbrushStamp>,
    object: ParsedStampObject,
    options: StampRenderOptions,
    pathIndex: number,
): Pick<StampStroke, "unionPathD" | "unionFill"> | null {
    if (stamps.length === 0) return null;
    const placements = stampsToUnionPlacementPayloads(
        stamps,
        object,
        options,
        pathIndex,
    );
    const unioned = unionPlacementPayloads({ placements });
    if (!unioned) return null;
    return { unionPathD: unioned.pathD, unionFill: unioned.fill };
}

export function applyStampStrokeUnion(
    stroke: StampStroke,
    object: ParsedStampObject,
    options: StampRenderOptions,
    pathIndex: number,
): StampStroke {
    const unioned = mergeStampStrokeUnion(
        stroke.stamps,
        object,
        options,
        pathIndex,
    );
    if (!unioned) return stroke;
    return { ...stroke, ...unioned };
}

export function cloneStampStrokes(strokes: StampStroke[]): StampStroke[] {
    return strokes.map((s) => ({
        id: s.id,
        stamps: s.stamps.map((st) => ({ ...st })),
        unionPathD: s.unionPathD,
        unionFill: s.unionFill,
    }));
}

export function stampsForRender(
    strokes: ReadonlyArray<StampStroke>,
    active: ActiveStampStroke | null,
): ArtbrushStamp[] {
    const out: ArtbrushStamp[] = [];
    for (const s of strokes) out.push(...s.stamps);
    if (active) out.push(...active.stamps);
    return out;
}

function stampInstancesMarkup(
    stamps: ReadonlyArray<ArtbrushStamp>,
    objects: ReadonlyArray<ParsedStampObject>,
    options: StampRenderOptions,
    pathIndex: number,
): string {
    if (stamps.length === 0) return "";
    return stamps
        .map((stamp, stampIndex) => {
            const object = objects[stamp.objectIndex] ?? objects[0];
            if (!object) return "";
            const scale =
                stampObjectScaleFactor(object.dimensions, options.objectSize) *
                Math.max(0.05, stamp.sizeFactor || 1);
            const rotationDeg = getStampRotationDegrees(
                stamp,
                stampIndex,
                pathIndex,
                object,
                options,
            );
            const transform = stampPlacementTransform(
                stamp,
                scale,
                object.dimensions,
                options.anchor,
                rotationDeg,
                getStampPerpendicularScatter(
                    stamp,
                    pathIndex,
                    stampIndex,
                    options.perpendicularScatterPx,
                ),
            );
            const fillColor = stampFillColor(options, pathIndex, stampIndex);
            const inner = fillColor
                ? applyFillToSvgContent(object.innerContent, fillColor)
                : object.innerContent;
            return `<g transform="${escapeSvgAttr(transform)}">${inner}</g>`;
        })
        .join("\n  ");
}

export function buildStampMarkup(
    stampStrokes: ReadonlyArray<StampStroke>,
    activeStampStroke: ActiveStampStroke | null,
    objects: ReadonlyArray<ParsedStampObject>,
    options: StampRenderOptions,
): string {
    if (objects.length === 0) return "";
    const parts: string[] = [];
    stampStrokes.forEach((stroke, pathIndex) => {
        if (stroke.unionPathD) {
            const fill = escapeSvgAttr(stroke.unionFill ?? "#000000");
            parts.push(
                `<path class="artbrush-stamp-union" d="${escapeSvgAttr(stroke.unionPathD)}" fill="${fill}"/>`,
            );
        } else {
            const block = stampInstancesMarkup(
                stroke.stamps,
                objects,
                options,
                pathIndex,
            );
            if (block) parts.push(block);
        }
    });
    if (activeStampStroke) {
        const block = stampInstancesMarkup(
            activeStampStroke.stamps,
            objects,
            options,
            stampStrokes.length,
        );
        if (block) parts.push(block);
    }
    return parts.join("\n  ");
}
